import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faFilter } from '@fortawesome/free-solid-svg-icons';
import CustomDatePicker from '../common/CustomDatePicker.js';

const FILTER_ANCHOR_GAP_PX = 4;

/**
 * Positions a popover in viewport coordinates below an anchor element.
 * Uses a portal so nested scroll/sticky headers cannot offset the popover.
 */
const FilterPopover = ({ getAnchorEl, alignRight, className, innerRef, children }) => {
  const [position, setPosition] = useState(null);

  const updatePosition = useCallback(() => {
    const anchor = getAnchorEl?.();
    if (!anchor) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    if (alignRight) {
      setPosition({
        top: rect.bottom + FILTER_ANCHOR_GAP_PX,
        left: undefined,
        right: window.innerWidth - rect.right,
      });
    } else {
      setPosition({
        top: rect.bottom + FILTER_ANCHOR_GAP_PX,
        left: rect.left,
        right: undefined,
      });
    }
  }, [getAnchorEl, alignRight]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useEffect(() => {
    window.addEventListener('resize', updatePosition);
    document.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      document.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  if (!position) {
    return null;
  }

  return createPortal(
    <div
      ref={innerRef}
      className={['filter-input', className].filter(Boolean).join(' ')}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        right: position.right,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
};

/**
 * Context menu component for column headers
 */
export const ContextMenu = ({ x, y, column, sortState, onSort, onFilter, onClose, hasActiveFilter }) => {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const isCurrentlySorted = sortState.columnKey === column.key;
  const sortDirection = isCurrentlySorted ? sortState.direction : null;
  const hasFilter = hasActiveFilter(column);

  return (
    <div 
      ref={menuRef}
      className="context-menu"
      style={{ position: 'fixed', left: x, top: y, zIndex: 1000 }}
      onClick={(e) => e.stopPropagation()}
    >
      {column.sortable && (
        <>
          <div 
            className="context-menu-item"
            onClick={() => {
              onSort('asc');
              onClose();
            }}
          >
            Order Ascending
            {sortDirection === 'asc' && (
              <FontAwesomeIcon icon={faCheck} className="context-menu-check" />
            )}
          </div>
          <div 
            className="context-menu-item"
            onClick={() => {
              onSort('desc');
              onClose();
            }}
          >
            Order Descending
            {sortDirection === 'desc' && (
              <FontAwesomeIcon icon={faCheck} className="context-menu-check" />
            )}
          </div>
        </>
      )}
      {column.filterable && (
        <div 
          className="context-menu-item"
          onClick={() => {
            onFilter();
            onClose();
          }}
        >
          Filter
          {hasFilter && (
            <FontAwesomeIcon icon={faCheck} className="context-menu-check" />
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Text filter input component
 */
export const TextFilter = ({ value, onChange, onClose, getAnchorEl, alignRight }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        onChange(inputValue);
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputValue, onChange, onClose]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onChange(inputValue);
      onClose();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <FilterPopover getAnchorEl={getAnchorEl} alignRight={alignRight} innerRef={inputRef}>
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyPress}
        placeholder="Search..."
        className="filter-text-input"
      />
    </FilterPopover>
  );
};

/**
 * Date range filter component
 */
export const DateRangeFilter = ({ value, onChange, onClose, getAnchorEl, alignRight }) => {
  const [dateRange, setDateRange] = useState(value || { startDate: '', endDate: '' });
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        onChange(dateRange);
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dateRange, onChange, onClose]);

  const handleStartDateChange = (date) => {
    const newRange = { ...dateRange, startDate: date };
    setDateRange(newRange);
  };

  const handleEndDateChange = (date) => {
    const newRange = { ...dateRange, endDate: date };
    setDateRange(newRange);
  };

  const handleApply = () => {
    onChange(dateRange);
    onClose();
  };

  const handleClear = () => {
    const clearedRange = { startDate: '', endDate: '' };
    setDateRange(clearedRange);
    onChange(clearedRange);
    onClose();
  };

  return (
    <FilterPopover
      getAnchorEl={getAnchorEl}
      alignRight={alignRight}
      className="date-range-filter"
      innerRef={containerRef}
    >
      <div className="date-range-inputs">
        <CustomDatePicker
          label="From"
          value={dateRange.startDate}
          onChange={handleStartDateChange}
          max={dateRange.endDate || undefined}
        />
        <CustomDatePicker
          label="To"
          value={dateRange.endDate}
          onChange={handleEndDateChange}
          min={dateRange.startDate || undefined}
        />
      </div>
      <div className="filter-actions">
        <button onClick={handleClear} className="filter-clear-btn">Clear</button>
        <button onClick={handleApply} className="filter-apply-btn">Apply</button>
      </div>
    </FilterPopover>
  );
};

/**
 * Enum filter component with checkboxes
 */
export const EnumFilter = ({ value, onChange, onClose, enumOptions, getAnchorEl, alignRight }) => {
  const [selections, setSelections] = useState(value || {});
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        onChange(selections);
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selections, onChange, onClose]);

  const handleToggle = (option) => {
    setSelections(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const handleSelectAll = () => {
    const allSelected = {};
    enumOptions.forEach(option => {
      allSelected[option] = true;
    });
    setSelections(allSelected);
  };

  const handleClearAll = () => {
    setSelections({});
  };

  const handleApply = () => {
    onChange(selections);
    onClose();
  };

  return (
    <FilterPopover
      getAnchorEl={getAnchorEl}
      alignRight={alignRight}
      className="enum-filter"
      innerRef={containerRef}
    >
      <div className="enum-filter-header">
        <button onClick={handleSelectAll} className="filter-select-all">Select All</button>
        <button onClick={handleClearAll} className="filter-clear-all">Clear All</button>
      </div>
      <div className="enum-filter-options">
        {enumOptions.map(option => (
          <label key={option} className="enum-option">
            <input
              type="checkbox"
              checked={!!selections[option]}
              onChange={() => handleToggle(option)}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
      <div className="filter-actions">
        <button onClick={handleApply} className="filter-apply-btn">Apply</button>
      </div>
    </FilterPopover>
  );
};

/**
 * Filter icon component
 */
export const FilterIcon = ({ onClick, hasFilter, hasDisabledFilter }) => {
  const getClassName = () => {
    if (hasFilter) return 'filter-icon filter-icon-active';
    if (hasDisabledFilter) return 'filter-icon filter-icon-disabled';
    return 'filter-icon';
  };

  const getTitle = () => {
    if (hasFilter) return 'Filter active - Click to disable';
    if (hasDisabledFilter) return 'Filter disabled - Click to enable';
    return 'No filter - Click to add filter';
  };

  return (
    <button 
      className={getClassName()}
      onClick={onClick}
      title={getTitle()}
    >
      <FontAwesomeIcon icon={faFilter} />
    </button>
  );
}; 